import {
  RECURRING_REPORT_TYPES,
  Report,
  ReportRecurStatus,
  ReportStatus,
} from 'dashboard/models/report';
import { ReportAdverseMedia } from 'dashboard/models/report/adverse-media';
import { ReportBusinessAdverseMedia } from 'dashboard/models/report/business-adverse-media';
import { ReportBusinessWatchlist } from 'dashboard/models/report/business-watchlist';
import { ReportCryptoAddressWatchlist } from 'dashboard/models/report/crypto-address-watchlist';
import { ReportPoliticallyExposedPerson } from 'dashboard/models/report/politically-exposed-person';
import { ReportWatchlist } from 'dashboard/models/report/watchlist';
import Sticker from 'design-system/components/Sticker';
import { ApiModel } from 'lib/interfaces/api';

interface ReportRecurStatusSticker {
  report: Report;
  verbose?: boolean;
}

const reportHasMatch = (report: Report) => {
  if (
    report.attributes.status === ReportStatus.Pending ||
    report.attributes.status === ReportStatus.Errored
  ) {
    return false;
  }

  switch (report.type) {
    case ApiModel.ReportAdverseMedia: {
      return ((report as ReportAdverseMedia).attributes['related-sources'] ?? []).length > 0;
    }
    case ApiModel.ReportBusinessAdverseMedia: {
      return (
        ((report as ReportBusinessAdverseMedia).attributes['related-sources'] ?? []).length > 0
      );
    }
    case ApiModel.ReportBusinessWatchlist: {
      return ((report as ReportBusinessWatchlist).attributes['matched-lists'] ?? []).length > 0;
    }
    case ApiModel.ReportCryptoAddressWatchlist: {
      return ((report as ReportCryptoAddressWatchlist).attributes['result'] ?? []).length > 0;
    }
    case ApiModel.ReportPoliticallyExposedPerson: {
      return ((report as ReportPoliticallyExposedPerson).attributes['result'] ?? []).length > 0;
    }
    case ApiModel.ReportWatchlist: {
      return ((report as ReportWatchlist).attributes['matched-lists'] ?? []).length > 0;
    }
    default:
      return false;
  }
};

const ReportRecurStatusSticker = ({ report, verbose = false }: ReportRecurStatusSticker) => {
  const isRecurringReport = RECURRING_REPORT_TYPES.includes(report.type);
  const isRedacted = report?.attributes?.['redacted-at'] != null;

  const {
    'recur-status': recurStatus,
    'report-template-version-interval-seconds': intervalSeconds,
    'next-scheduled-at': nextScheduledAt,
  } = report.attributes;

  if (!isRecurringReport) {
    return null;
  } else if (isRedacted) {
    return <Sticker variant='basic'>Redacted</Sticker>;
  } else if (
    report.type === ApiModel.ReportCustomList &&
    recurStatus === ReportRecurStatus.Active
  ) {
    return <Sticker variant='success'>{verbose ? 'Continuous Monitoring' : 'On'}</Sticker>;
  } else if (intervalSeconds == null) {
    // returning null when verbose because we actually want sticker hidden if not labeled
    return verbose ? null : <Sticker variant='basic'>Off</Sticker>;
  } else if (recurStatus === ReportRecurStatus.Paused) {
    return <Sticker variant='caution'>Paused</Sticker>;
  } else if (reportHasMatch(report) && nextScheduledAt == null) {
    return <Sticker variant='caution'>Paused - Active Matches</Sticker>;
  } else if (nextScheduledAt != null) {
    return <Sticker variant='success'>{verbose ? 'Continuous Monitoring' : 'On'}</Sticker>;
  } else {
    return null;
  }
};

export default ReportRecurStatusSticker;
